/**
 * The cabinet mesh rebuild for a live product-form update: dimension
 * diffing, product-data merging, delete + re-render, position nudges,
 * side-attachment repositioning, and wall clamping.
 * Only `applyCabinetFormUpdate` is public; the rest are pipeline internals.
 * The edit-session steps around this rebuild live in `editSession.ts`.
 */
import * as THREE from 'three';
import {hasUsableShelfPositions} from 'components/customer/Preview3DCommon/shelves/common';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {applyImagePath} from 'components/customer/RoomPlanner/helpers/scene';
import {
    isFillerFixingSideFlipped,
    parsedFillerFlipSourceFromGroup,
} from 'components/customer/RoomPlanner/helpers/fillerSwitching';
import {syncLShapedDepthAliasesOnCabinet} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {
    applyExteriorThicknessPositionNudge,
    applyFlippedFillerWidthResizeNudge,
    findSideAttachmentParentForAccessory,
    getAttachedSideFillersAndVermins,
    realignSideAttachmentToParent,
    repositionAttachedSideProductsAfterDimensionChange,
    SideAttachment,
    SideAttachmentParent,
} from 'components/customer/RoomPlanner/helpers/sideAttachmentReposition';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {renderCabinetGroup} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {
    CabinetMesh,
    RoomPlannerProduct,
    RoomPlannerProductWithTemplate,
    RoomType,
} from 'components/customer/RoomPlanner/types';
import {
    captureAttachedSideOriginalPositions,
    captureEditSessionOriginals,
} from './editSession';
import {
    ApplyCabinetFormUpdateParams,
    ApplyCabinetFormUpdateResult,
    CabinetDimensionDeltas,
    CabinetDimensionFields,
    EditSessionOriginalRefs,
    PreviousCabinetDimensions,
} from './types';

// ---------------------------------------------------------------------------
// Dimension diffing
// ---------------------------------------------------------------------------

/**
 * True when a size delta should move a mesh (non-zero and numeric).
 * @param {number} value - Delta in mm
 * @return {boolean} Whether the delta is usable
 */
const hasFiniteDelta = (value: number): boolean => {
    return value !== 0 && !isNaN(value);
};

/**
 * Width used when comparing an edit against the cabinet's previous size.
 * L-shaped products use left width (with width2 / width fallbacks).
 * @param {object} values - Product fields
 * @param {boolean} isLShapedProduct - Whether the cabinet is L-shaped
 * @return {number} Width in mm
 */
const getCabinetWidthForDimensionDiff = (
    values: CabinetDimensionFields,
    isLShapedProduct: boolean
): number => {
    if (isLShapedProduct) {
        return Number(
            values.cabinet_left_width ??
                values.cabinet_width ??
                values.width2 ??
                values.width ??
                0
        );
    }

    return Number(values.cabinet_width ?? 0);
};

/**
 * Depth used when comparing an edit against the cabinet's previous size.
 * L-shaped products use right width (with width / cabinet_depth fallbacks).
 * @param {object} values - Product fields
 * @param {boolean} isLShapedProduct - Whether the cabinet is L-shaped
 * @return {number} Depth in mm
 */
const getCabinetDepthForDimensionDiff = (
    values: CabinetDimensionFields,
    isLShapedProduct: boolean
): number => {
    if (isLShapedProduct) {
        return Number(
            values.cabinet_right_width ??
                values.width ??
                values.cabinet_depth ??
                0
        );
    }

    return Number(values.cabinet_depth ?? 0);
};

/**
 * L-shaped left (return) depth for dimension diffs.
 * @param {object} values - Product fields
 * @return {number} Left depth in mm
 */
const getLShapedLeftDepthForDimensionDiff = (
    values: CabinetDimensionFields
): number => {
    return Number(
        values.cabinet_left_depth ??
            values.cabinet_width_depth ??
            values.width_depth ??
            0
    );
};

/**
 * L-shaped right (return) depth for dimension diffs.
 * @param {object} values - Product fields
 * @return {number} Right depth in mm
 */
const getLShapedRightDepthForDimensionDiff = (
    values: CabinetDimensionFields
): number => {
    return Number(
        values.cabinet_right_depth ??
            values.cabinet_length_depth ??
            values.length_depth ??
            0
    );
};

/**
 * Reads the live mesh's dimensions before it is deleted and re-rendered.
 * @param {CabinetMesh} cabinet - Cabinet being updated
 * @param {boolean} isLShapedCabinet - Whether the cabinet is L-shaped
 * @return {object} Previous dimensions
 */
const readPreviousCabinetDimensions = (
    cabinet: CabinetMesh,
    isLShapedCabinet: boolean
): PreviousCabinetDimensions => {
    const values = cabinet.userData.values as CabinetDimensionFields;

    return {
        previousCabinetDepth: getCabinetDepthForDimensionDiff(
            values,
            isLShapedCabinet
        ),
        previousExteriorThickness: Number(values.cabinet_ext_thickness || 0),
        previousCarcaseThickness: Number(values.cabinet_carc_thickness || 0),
        previousCabinetHeight: Number(values.cabinet_height),
        previousCabinetWidth: getCabinetWidthForDimensionDiff(
            values,
            isLShapedCabinet
        ),
        previousLeftDepth: isLShapedCabinet
            ? getLShapedLeftDepthForDimensionDiff(values)
            : 0,
        previousRightDepth: isLShapedCabinet
            ? getLShapedRightDepthForDimensionDiff(values)
            : 0,
    };
};

const sessionDelta = (finalValue: number, originalValue: number): number => {
    return finalValue - originalValue;
};

/**
 * Session-relative and incremental size deltas used to nudge the re-rendered
 * cabinet and its side attachments.
 * @param {object} params - Previous/final sizes and edit-session originals
 * @return {object} Deltas in mm
 */
const getCabinetDimensionDeltas = ({
    previous,
    finalProductData,
    isLShapedCabinet,
    fromBackup,
    originalDepth,
    originalWidth,
    originalHeight,
    originalExteriorThickness,
}: {
    previous: PreviousCabinetDimensions;
    finalProductData: CabinetDimensionFields;
    isLShapedCabinet: boolean;
    fromBackup?: boolean;
    originalDepth: number;
    originalWidth: number;
    originalHeight: number;
    originalExteriorThickness: number;
}): CabinetDimensionDeltas => {
    const finalCabinetWidth = getCabinetWidthForDimensionDiff(
        finalProductData,
        isLShapedCabinet
    );
    const finalCabinetDepth = getCabinetDepthForDimensionDiff(
        finalProductData,
        isLShapedCabinet
    );
    const finalLeftDepth = isLShapedCabinet
        ? getLShapedLeftDepthForDimensionDiff(finalProductData)
        : 0;
    const finalRightDepth = isLShapedCabinet
        ? getLShapedRightDepthForDimensionDiff(finalProductData)
        : 0;

    // Session deltas are vs the size captured at edit start. Algebraically
    // `final - previous - (original - previous)` is `final - original`.
    let depthDifference = sessionDelta(finalCabinetDepth, originalDepth);
    let exteriorThicknessDifference = sessionDelta(
        Number(finalProductData.cabinet_ext_thickness || 0),
        originalExteriorThickness
    );
    let widthDifference = sessionDelta(finalCabinetWidth, originalWidth);
    const heightDifference = sessionDelta(
        Number(finalProductData.cabinet_height),
        originalHeight
    );

    if (fromBackup) {
        depthDifference = 0;
        exteriorThicknessDifference = 0;
        widthDifference = 0;
    }

    const incrementalExteriorThicknessDelta =
        Number(finalProductData.cabinet_ext_thickness || 0) -
        previous.previousExteriorThickness;
    const incrementalCarcaseThicknessDelta =
        Number(finalProductData.cabinet_carc_thickness || 0) -
        previous.previousCarcaseThickness;
    const incrementalDepthDelta =
        finalCabinetDepth - previous.previousCabinetDepth;
    const incrementalWidthDelta =
        finalCabinetWidth - previous.previousCabinetWidth;
    const incrementalLeftDepthDelta = isLShapedCabinet
        ? finalLeftDepth - previous.previousLeftDepth
        : 0;
    const incrementalRightDepthDelta = isLShapedCabinet
        ? finalRightDepth - previous.previousRightDepth
        : 0;

    return {
        depthDifference,
        widthDifference,
        heightDifference,
        exteriorThicknessDifference,
        incrementalExteriorThicknessDelta,
        incrementalCarcaseThicknessDelta,
        incrementalDepthDelta,
        incrementalWidthDelta,
        incrementalLeftDepthDelta,
        incrementalRightDepthDelta,
        hasLShapedAttachmentDimensionChange:
            isLShapedCabinet &&
            (hasFiniteDelta(incrementalWidthDelta) ||
                hasFiniteDelta(incrementalDepthDelta) ||
                hasFiniteDelta(incrementalLeftDepthDelta) ||
                hasFiniteDelta(incrementalRightDepthDelta)),
    };
};

// ---------------------------------------------------------------------------
// Product-data merging
// ---------------------------------------------------------------------------

const applyShelfTypeTransition = (
    mergedProductData: PreviewFormValues,
    productValuesForUpdate: PreviewFormValues,
    isLShapedCabinet: boolean
): PreviewFormValues => {
    if (typeof productValuesForUpdate.cabinet_simple_shelves === 'undefined') {
        return mergedProductData;
    }

    // Switching to Shape shelves: rectangular cabinets drop the simple shelf
    // data; L / corner keep the count and get their shelves array seeded evenly.
    if (!productValuesForUpdate.cabinet_simple_shelves) {
        if (!isLShapedCabinet) {
            return {
                ...mergedProductData,
                simple_shelves_amount: 0,
                simple_shelf_type: undefined,
            } as PreviewFormValues;
        }

        const seedAmount = Number(mergedProductData.simple_shelves_amount);
        const needsSeed =
            seedAmount > 0 &&
            !hasUsableShelfPositions(
                mergedProductData.shelves as {shelf_position: number}[],
                seedAmount,
                Number(mergedProductData.cabinet_height)
            );

        if (!needsSeed) return mergedProductData;

        const seedStyle = Number(mergedProductData.simple_shelf_type) || 0;
        const seedOffset = mergedProductData.shelf_set_back
            ? Number(mergedProductData.shelf_set_back)
            : 5;

        return {
            ...mergedProductData,
            shelves: Array.from({length: seedAmount}, (_, i) => ({
                shelf_style: seedStyle,
                shelf_position: Number(((i + 1) / (seedAmount + 1)).toFixed(4)),
                shelf_offset: seedOffset,
            })),
        } as PreviewFormValues;
    }

    // Switching to Plain shelves: clear shape shelf arrays if not in the update.
    return {
        ...mergedProductData,
        ...(!productValuesForUpdate.shelves && {
            shelves: undefined,
        }),
        ...(!productValuesForUpdate.upper_shelves && {
            upper_shelves: undefined,
        }),
        ...(!productValuesForUpdate.lower_shelves && {
            lower_shelves: undefined,
        }),
        ...(!productValuesForUpdate.middle_shelves && {
            middle_shelves: undefined,
        }),
    };
};

/**
 * Merges the selected product with the live form update, including material
 * images and shelf-type transitions.
 * @param {object} params - Selected product, form update, and cabinet flags
 * @return {object} Product payload to re-render
 */
const mergeProductDataForCabinetUpdate = ({
    productValue,
    productValuesForUpdate,
    isSwitchingCabinet,
    isLShapedCabinet,
}: {
    productValue: RoomPlannerProduct;
    productValuesForUpdate: PreviewFormValues;
    isSwitchingCabinet: boolean;
    isLShapedCabinet: boolean;
}): PreviewFormValues => {
    const baseProductData = {
        ...productValue,
        cabinet_ext_image: isSwitchingCabinet
            ? undefined
            : applyImagePath(productValuesForUpdate.ext_material?.image) ||
              productValue.cabinet_ext_image,
        cabinet_carc_image: isSwitchingCabinet
            ? undefined
            : applyImagePath(productValuesForUpdate.carc_material?.image) ||
              productValue.cabinet_carc_image,
        cabinet_ext_edge_image: isSwitchingCabinet
            ? undefined
            : applyImagePath(productValue.ext_edge?.image) ||
              productValue.cabinet_ext_edge_image,
        cabinet_carc_edge_image: isSwitchingCabinet
            ? undefined
            : applyImagePath(productValue.carc_edge?.image) ||
              productValue.cabinet_carc_edge_image,
    } as unknown as PreviewFormValues;

    if (baseProductData.drawer_amount != productValuesForUpdate.drawer_amount) {
        baseProductData.drawer_amount = productValuesForUpdate.drawer_amount;
        baseProductData.drawer_face_height =
            productValuesForUpdate.drawer_face_height;
    }

    const mergedProductData = {
        ...baseProductData,
        ...productValuesForUpdate,
        isSwitchingCabinet: false,
    };
    delete (mergedProductData as Record<string, unknown>)._restorePositionY;
    delete (mergedProductData as Record<string, unknown>)._restoreHeight;

    // Drop cached shaker-door detection from undo history; door/material
    // on the product payload are real fields and must be kept.
    if (
        !productValuesForUpdate.fromUndoOrRedo &&
        !productValuesForUpdate.fromBackup
    ) {
        delete (mergedProductData as Record<string, unknown>).shakerDoorInfo;
    }

    return applyShelfTypeTransition(
        mergedProductData,
        productValuesForUpdate,
        isLShapedCabinet
    );
};

/**
 * Builds the product payload passed to `renderCabinetGroup` after a form update.
 * @param {object} params - Merged product, cabinet mesh, and switch flag
 * @return {object} Product to re-render
 */
const buildProductForCabinetRerender = ({
    finalProductData,
    cabinet,
    productValue,
    isSwitchingCabinet,
}: {
    finalProductData: PreviewFormValues;
    cabinet: CabinetMesh;
    productValue: RoomPlannerProduct;
    isSwitchingCabinet: boolean;
}): RoomPlannerProductWithTemplate => {
    const productWithNewDimensions = {
        ...finalProductData,
        duplicate_id: cabinet.userData.values?.duplicate_id,
        ...(!isSwitchingCabinet
            ? {
                  template_3d: (
                      productValue as unknown as RoomPlannerProductWithTemplate
                  ).template_3d,
              }
            : {}),
        isDoorOrDrawerOpen: cabinet.userData.isDoorOrDrawerOpen,
    } as unknown as RoomPlannerProductWithTemplate;

    if (productWithNewDimensions.drawer_face_height) {
        productWithNewDimensions.drawer_face_heights =
            productWithNewDimensions.drawer_face_height.join(',');
    }

    return productWithNewDimensions;
};

// ---------------------------------------------------------------------------
// Position nudges
// ---------------------------------------------------------------------------

const applyDepthPositionNudge = (
    updatedCabinetGroup: CabinetMesh,
    cabinet: CabinetMesh,
    cabinetFacingDirection: string,
    depthDifference: number,
    scale: number
) => {
    const offset = depthDifference * scale;
    const isVermin = cabinet.userData.isVerminProduct;

    if (
        cabinetFacingDirection === 'front' ||
        cabinetFacingDirection === 'normal'
    ) {
        updatedCabinetGroup.position.z += offset;
        if (isVermin) {
            updatedCabinetGroup.position.z -= offset;
        }
        return;
    }

    if (cabinetFacingDirection === 'back') {
        updatedCabinetGroup.position.z -= offset;
        if (isVermin) {
            updatedCabinetGroup.position.z += offset;
        }
        return;
    }

    if (cabinetFacingDirection === 'left') {
        updatedCabinetGroup.position.x += offset;
        if (isVermin) {
            updatedCabinetGroup.position.x -= offset;
        }
        return;
    }

    if (cabinetFacingDirection === 'right') {
        updatedCabinetGroup.position.x -= offset;
        if (isVermin) {
            updatedCabinetGroup.position.x += offset;
        }
    }
};

const applyFillerWidthPositionNudge = (
    updatedCabinetGroup: CabinetMesh,
    cabinet: CabinetMesh,
    widthDifference: number,
    scale: number
) => {
    const flipped = isFillerFixingSideFlipped(cabinet);

    if (flipped) {
        applyFlippedFillerWidthResizeNudge(
            updatedCabinetGroup,
            cabinet.userData.currentDirection || 'front',
            widthDifference * scale
        );
    }

    const flipSource = parsedFillerFlipSourceFromGroup(updatedCabinetGroup);

    if (flipSource === 'wall') {
        if (
            cabinet.userData.currentDirection === 'front' ||
            cabinet.userData.currentDirection === 'normal'
        ) {
            updatedCabinetGroup.position.x += widthDifference * scale;
        }
    }
};

/**
 * Restores Y after an undo that stored the previous height and position.
 * @param {object} productValuesForUpdatePayload - Form update
 * @param {object} finalProductData - Merged product
 * @param {object} dimensions - Room scale
 * @param {CabinetMesh} cabinetGroup - Re-rendered cabinet
 * @return {void}
 */
const applyRestorePositionY = (
    productValuesForUpdatePayload: Record<string, unknown>,
    finalProductData: {cabinet_height?: number | string},
    dimensions: {scale: number},
    cabinetGroup: CabinetMesh
) => {
    const restoreY = productValuesForUpdatePayload._restorePositionY;
    const restoreHeight = productValuesForUpdatePayload._restoreHeight;

    const isValidRestoreY = typeof restoreY === 'number' && !isNaN(restoreY);
    const isValidRestoreHeight =
        typeof restoreHeight === 'number' && !isNaN(restoreHeight);

    if (isValidRestoreY) {
        const cabinetHeight =
            Number(finalProductData.cabinet_height) * dimensions.scale;
        const oldCabinetHeight = isValidRestoreHeight
            ? Number(restoreHeight) * dimensions.scale
            : cabinetHeight;
        const heightDelta = oldCabinetHeight - cabinetHeight;
        if (
            !isNaN(cabinetHeight) &&
            !isNaN(oldCabinetHeight) &&
            !isNaN(heightDelta)
        ) {
            cabinetGroup.position.y = restoreY + heightDelta;
            autoSave(cabinetGroup, true);
        }
    }
};

/**
 * Nudges the re-rendered cabinet for depth, filler width, thickness, and height.
 * @param {object} params - Mesh, previous cabinet, deltas, and scale
 * @return {object} Deltas after height may be zeroed for upper / backup
 */
const applyCabinetDimensionPositionNudges = ({
    updatedCabinetGroup,
    cabinet,
    isLShapedCabinet,
    deltas,
    scale,
    fromBackup,
}: {
    updatedCabinetGroup: CabinetMesh;
    cabinet: CabinetMesh;
    isLShapedCabinet: boolean;
    deltas: CabinetDimensionDeltas;
    scale: number;
    fromBackup?: boolean;
}): CabinetDimensionDeltas => {
    const cabinetFacingDirection = cabinet.userData.currentDirection;
    const nextDeltas = {...deltas};

    if (
        fromBackup ||
        !updatedCabinetGroup.userData.isUpperProduct ||
        updatedCabinetGroup.userData.isBulkheadComponent
    ) {
        nextDeltas.heightDifference = 0;
    }

    // Normal cabinets only: adjust position when editing depth.
    // L-shaped products map Right Width through the depth delta, so
    // applying this here would incorrectly move/detach attachments.
    if (!isLShapedCabinet && hasFiniteDelta(nextDeltas.depthDifference)) {
        applyDepthPositionNudge(
            updatedCabinetGroup,
            cabinet,
            cabinetFacingDirection,
            nextDeltas.depthDifference,
            scale
        );
    }

    // Fillers only: nudge position when width changes (unflipped). Re-apply flip
    // after render when already flipped — keep position, only restore negative scale.x.
    if (
        hasFiniteDelta(nextDeltas.widthDifference) &&
        (cabinet.userData.isFillerProduct || cabinet.userData.isVerminProduct)
    ) {
        applyFillerWidthPositionNudge(
            updatedCabinetGroup,
            cabinet,
            nextDeltas.widthDifference,
            scale
        );
    }

    if (hasFiniteDelta(nextDeltas.exteriorThicknessDifference)) {
        applyExteriorThicknessPositionNudge(
            updatedCabinetGroup,
            cabinetFacingDirection,
            nextDeltas.exteriorThicknessDifference,
            scale,
            false
        );
    }

    if (hasFiniteDelta(nextDeltas.heightDifference)) {
        updatedCabinetGroup.position.y -= nextDeltas.heightDifference * scale;
    }

    return nextDeltas;
};

// ---------------------------------------------------------------------------
// Side attachments
// ---------------------------------------------------------------------------

/**
 * Parent cabinet for a side-attached filler / vermin, if this mesh is one.
 * @param {CabinetMesh} cabinet - Accessory being edited
 * @param {Array} allCabinets - Scene cabinets
 * @return {object | null} Parent and attachment descriptor
 */
const resolveSideAttachmentParent = (
    cabinet: CabinetMesh,
    allCabinets: CabinetMesh[]
): SideAttachmentParent | null => {
    if (!(
        cabinet.userData.isFillerProduct || cabinet.userData.isVerminProduct
    )) {
        return null;
    }

    return findSideAttachmentParentForAccessory(cabinet, allCabinets);
};

const hasAccessoryThicknessChange = (
    deltas: CabinetDimensionDeltas
): boolean => {
    return (
        hasFiniteDelta(deltas.exteriorThicknessDifference) ||
        hasFiniteDelta(deltas.incrementalExteriorThicknessDelta) ||
        hasFiniteDelta(deltas.incrementalCarcaseThicknessDelta)
    );
};

/**
 * Repositions side-attached fillers / vermin after a parent size change, and
 * re-snaps an accessory to its parent when its own thickness changed.
 * @param {object} params - Updated mesh, attachments, deltas, and session refs
 * @return {void}
 */
const applySideAttachmentUpdates = ({
    updatedCabinetGroup,
    attachedSideProducts,
    allCabinets,
    fromBackup,
    refs,
    isLShapedCabinet,
    deltas,
    sideAttachmentParent,
}: {
    updatedCabinetGroup: CabinetMesh;
    attachedSideProducts: SideAttachment[];
    allCabinets: CabinetMesh[];
    fromBackup?: boolean;
    refs: EditSessionOriginalRefs;
    isLShapedCabinet: boolean;
    deltas: CabinetDimensionDeltas;
    sideAttachmentParent: SideAttachmentParent | null;
}) => {
    const restoredAttachedSideOriginalPositions =
        repositionAttachedSideProductsAfterDimensionChange({
            updatedCabinetGroup,
            attachedSideProducts,
            allCabinets,
            fromBackup,
            originalPositions: refs.attachedSideOriginalPositionsRef.current,
            isLShapedCabinet,
            widthDifference: deltas.widthDifference,
            depthDifference: deltas.depthDifference,
            incrementalWidthDelta: deltas.incrementalWidthDelta,
            incrementalDepthDelta: deltas.incrementalDepthDelta,
            exteriorThicknessDifference: deltas.exteriorThicknessDifference,
            incrementalExteriorThicknessDelta:
                deltas.incrementalExteriorThicknessDelta,
            incrementalLeftDepthDelta: deltas.incrementalLeftDepthDelta,
            incrementalRightDepthDelta: deltas.incrementalRightDepthDelta,
            hasLShapedAttachmentDimensionChange:
                deltas.hasLShapedAttachmentDimensionChange,
        });

    if (restoredAttachedSideOriginalPositions) {
        refs.attachedSideOriginalPositionsRef.current = null;
    }

    if (
        sideAttachmentParent &&
        hasAccessoryThicknessChange(deltas) &&
        !fromBackup
    ) {
        realignSideAttachmentToParent(
            sideAttachmentParent.parent,
            sideAttachmentParent.attachment,
            updatedCabinetGroup,
            allCabinets
        );
    }
};

// ---------------------------------------------------------------------------
// Wall clamping
// ---------------------------------------------------------------------------

/**
 * Clamps L-shaped, key-shape, and upper cabinets back inside the room walls.
 * @param {CabinetMesh} updatedCabinetGroup - Re-rendered cabinet
 * @param {string | null} roomType - Current room shape
 * @param {object} camera - Scene camera
 * @return {void}
 */
const clampUpdatedCabinetToRoom = (
    updatedCabinetGroup: CabinetMesh,
    roomType: RoomType | null,
    camera?: THREE.Camera
) => {
    if (
        !updatedCabinetGroup.userData.isLShapedProduct &&
        !updatedCabinetGroup.userData.isKeyShapePanel &&
        !(
            updatedCabinetGroup.userData.isUpperProduct &&
            !updatedCabinetGroup.userData.isFillerProduct
        )
    ) {
        return;
    }

    const scene = updatedCabinetGroup.parent as THREE.Scene | null;
    const wallGroup = scene?.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as
        | THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>
        | undefined;

    if (!scene || !wallGroup || !camera) {
        return;
    }

    const clampedResult = productClamping(
        updatedCabinetGroup,
        wallGroup,
        camera,
        new THREE.Vector2(0, 0),
        updatedCabinetGroup.position.clone(),
        roomType ?? '',
        !!updatedCabinetGroup.userData.isUpperProduct,
        true
    );

    updatedCabinetGroup.position.copy(clampedResult.clamped);
};

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

const copyRuntimeUserData = (
    previousCabinet: CabinetMesh,
    updatedCabinetGroup: CabinetMesh
) => {
    updatedCabinetGroup.userData.isDoorOrDrawerOpen =
        previousCabinet.userData.isDoorOrDrawerOpen;
    updatedCabinetGroup.userData.fillerFlipSource =
        parsedFillerFlipSourceFromGroup(previousCabinet);
    syncLShapedDepthAliasesOnCabinet(updatedCabinetGroup);
};

/**
 * Deletes and re-renders a cabinet from a product-form update, then nudges
 * position, attachments, and wall clamping to match the previous mesh.
 * @param {object} params - Cabinet, form payload, session refs, and room
 * @return {object | null} Updated mesh and attachments
 */
export const applyCabinetFormUpdate = ({
    cabinet,
    productValue,
    productValuesForUpdate,
    isSwitchingCabinet,
    cabinetGroupListRef,
    refs,
    roomId,
    roomType,
    camera,
}: ApplyCabinetFormUpdateParams): ApplyCabinetFormUpdateResult | null => {
    const isLShapedCabinet = Boolean(cabinet.userData.isLShapedProduct);
    const previous = readPreviousCabinetDimensions(cabinet, isLShapedCabinet);
    const isStartingEditSession = captureEditSessionOriginals(
        refs,
        previous,
        productValuesForUpdate.fromBackup
    );

    const allCabinets = cabinetGroupListRef.current ?? [];
    const attachedSideProducts = getAttachedSideFillersAndVermins(
        cabinet,
        allCabinets
    );

    if (isStartingEditSession && attachedSideProducts.length > 0) {
        captureAttachedSideOriginalPositions(
            attachedSideProducts,
            refs.attachedSideOriginalPositionsRef,
            true
        );
    }

    const sideAttachmentParent = resolveSideAttachmentParent(
        cabinet,
        allCabinets
    );

    deleteCabinetMesh(cabinet, false, cabinetGroupListRef);

    const dimensions = getRoomDimensionsComputed();
    const switching = Boolean(isSwitchingCabinet);
    const finalProductData = mergeProductDataForCabinetUpdate({
        productValue,
        productValuesForUpdate,
        isSwitchingCabinet: switching,
        isLShapedCabinet,
    });
    const productWithNewDimensions = buildProductForCabinetRerender({
        finalProductData,
        cabinet,
        productValue,
        isSwitchingCabinet: switching,
    });

    const deltas = getCabinetDimensionDeltas({
        previous,
        finalProductData,
        isLShapedCabinet,
        fromBackup: productValuesForUpdate.fromBackup,
        originalDepth: refs.originalDepthRef?.current || 0,
        originalWidth: refs.originalWidthRef?.current ?? 0,
        originalHeight: refs.originalHeightRef?.current || 0,
        originalExteriorThickness:
            refs.originalExteriorThicknessRef.current || 0,
    });

    const {cabinetGroup: updatedCabinetGroup} = renderCabinetGroup(
        productWithNewDimensions,
        dimensions,
        roomId,
        cabinetGroupListRef
    );

    copyRuntimeUserData(cabinet, updatedCabinetGroup);

    const appliedDeltas = applyCabinetDimensionPositionNudges({
        updatedCabinetGroup,
        cabinet,
        isLShapedCabinet,
        deltas,
        scale: dimensions.scale,
        fromBackup: productValuesForUpdate.fromBackup,
    });

    applyRestorePositionY(
        productValuesForUpdate as Record<string, unknown>,
        finalProductData,
        dimensions,
        updatedCabinetGroup
    );

    applySideAttachmentUpdates({
        updatedCabinetGroup,
        attachedSideProducts,
        allCabinets: cabinetGroupListRef.current ?? [],
        fromBackup: productValuesForUpdate.fromBackup,
        refs,
        isLShapedCabinet,
        deltas: appliedDeltas,
        sideAttachmentParent,
    });

    clampUpdatedCabinetToRoom(updatedCabinetGroup, roomType, camera);

    return {updatedCabinetGroup, attachedSideProducts};
};
